import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ filters, onFiltersChange, resultCount = 0, isCollapsed = false, onToggleCollapse }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const cities = [
    'Atlanta, GA', 'Birmingham, AL', 'Charlotte, NC', 'Columbia, SC', 
    'Jackson, MS', 'Memphis, TN', 'Nashville, TN', 'New Orleans, LA'
  ];

  const categories = [
    'Transportation', 'Energy Management', 'Water Systems', 'Waste Management',
    'Public Safety', 'Digital Infrastructure', 'Environmental Monitoring', 'Urban Planning'
  ];

  const timelines = [
    '2-4 weeks', '1-2 months', '2-3 months', '3-6 months', '6+ months'
  ];

  const skills = [
    'JavaScript', 'Python', 'React', 'Node.js', 'Data Analysis', 'Machine Learning',
    'IoT Development', 'GIS Mapping', 'UI/UX Design', 'Project Management',
    'Business Analysis', 'Database Design', 'Mobile Development', 'Cloud Computing'
  ];

  const teamSizes = ['2-3 members', '4-5 members', '6-8 members', '8+ members'];
  const complexityLevels = ['Beginner', 'Intermediate', 'Advanced'];

  const handleFilterChange = (filterType, value, checked) => {
    const newFilters = { ...localFilters };
    
    if (filterType === 'searchTerm') {
      newFilters.searchTerm = value;
    } else if (filterType === 'sortBy') {
      newFilters.sortBy = value;
    } else {
      if (!newFilters?.[filterType]) {
        newFilters[filterType] = [];
      }
      
      if (checked) {
        newFilters[filterType] = [...newFilters?.[filterType], value];
      } else {
        newFilters[filterType] = newFilters?.[filterType]?.filter(item => item !== value);
      }
    }
    
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      searchTerm: '',
      cities: [],
      categories: [],
      timelines: [],
      skills: [],
      teamSizes: [],
      complexity: [],
      sortBy: 'relevance'
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    Object.keys(localFilters)?.forEach(key => {
      if (key !== 'searchTerm' && key !== 'sortBy' && localFilters?.[key]?.length > 0) {
        count += localFilters?.[key]?.length;
      }
    });
    return count;
  };

  const FilterSection = ({ title, items, filterKey, icon }) => (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Icon name={icon} size={16} className="text-muted-foreground" />
        <h3 className="font-medium text-foreground">{title}</h3>
      </div>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {items?.map((item, index) => (
          <Checkbox
            key={index}
            label={item}
            checked={localFilters?.[filterKey]?.includes(item) || false}
            onChange={(e) => handleFilterChange(filterKey, item, e?.target?.checked)}
            size="sm"
          />
        ))}
      </div>
    </div>
  );

  if (isCollapsed) {
    return (
      <div className="lg:hidden fixed top-20 left-4 z-40">
        <Button
          variant="default"
          size="sm"
          onClick={onToggleCollapse}
          iconName="Filter"
          iconPosition="left"
          className="shadow-elevated"
        >
          Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-fit sticky top-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Filters</h2>
          {getActiveFilterCount() > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {getActiveFilterCount()}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">{resultCount} results</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="lg:hidden"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>
      </div>
      {/* Search */}
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Search problems..."
          value={localFilters?.searchTerm || ''}
          onChange={(e) => handleFilterChange('searchTerm', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Sort */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
          <h3 className="font-medium text-foreground">Sort By</h3>
        </div>
        <select
          value={localFilters?.sortBy || 'relevance'}
          onChange={(e) => handleFilterChange('sortBy', e?.target?.value)}
          className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="relevance">Relevance</option>
          <option value="deadline">Deadline</option>
          <option value="complexity">Complexity</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>
      {/* Filter Sections */}
      <div className="space-y-6 max-h-96 overflow-y-auto">
        <FilterSection
          title="Cities"
          items={cities}
          filterKey="cities"
          icon="MapPin"
        />

        <FilterSection
          title="Categories"
          items={categories}
          filterKey="categories"
          icon="Tag"
        />

        <FilterSection
          title="Timeline"
          items={timelines}
          filterKey="timelines"
          icon="Clock"
        />

        <FilterSection
          title="Complexity"
          items={complexityLevels}
          filterKey="complexity"
          icon="TrendingUp"
        />

        <FilterSection
          title="Team Size"
          items={teamSizes}
          filterKey="teamSizes"
          icon="Users"
        />

        <FilterSection
          title="Required Skills"
          items={skills}
          filterKey="skills"
          icon="Code"
        />
      </div>
      {/* Clear Filters */}
      {getActiveFilterCount() > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
            iconName="RotateCcw"
            iconPosition="left"
            fullWidth
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;